import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Plus, Save, Send, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";

const uid = () => "m-" + Math.random().toString(36).slice(2, 10);

export default function CompassPlanBuilder() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState({
    gap_text: "", gap_evidence: "", gap_category: "",
    standard_text: "", standard_source: "",
    measures: [], resources_and_support: "",
    timeline_days: 60, check_in_dates: [],
  });
  const [warnings, setWarnings] = useState({});
  const [existingPlan, setExistingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const e = await api.get(`/compass/entry-requests/${entryId}`);
        setEntry(e.data);
        setForm((f) => ({
          ...f,
          gap_text: e.data.gap_description,
          gap_evidence: e.data.evidence_notes,
          gap_category: e.data.gap_category,
          measures: [{ id: uid(), text: "", notes: "" }],
        }));
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      }
    })();
  }, [entryId]);

  const save = async ({ submit = false } = {}) => {
    if (!form.gap_text.trim() || !form.standard_text.trim() || !form.standard_source.trim()) {
      toast.error("Gap, Standard, and Standard source are required.");
      return;
    }
    if (form.measures.length === 0 || form.measures.some((m) => !m.text.trim())) {
      toast.error("Each measure needs text.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entry_request_id: entryId,
        gap_text: form.gap_text,
        gap_evidence: form.gap_evidence,
        gap_category: form.gap_category || "other",
        standard_text: form.standard_text,
        standard_source: form.standard_source,
        measures: form.measures,
        resources_and_support: form.resources_and_support,
        timeline_days: form.timeline_days,
        check_in_dates: form.check_in_dates.length ? form.check_in_dates : undefined,
      };
      const { data } = await api.post("/compass/plans", payload);
      setWarnings(data.warnings || {});
      setExistingPlan(data.plan);
      if (data.plan.check_in_dates?.length) {
        setForm((f) => ({ ...f, check_in_dates: data.plan.check_in_dates }));
      }
      if (submit) {
        await api.post(`/compass/plans/${data.plan.id}/submit-for-review`);
        toast.success("Plan sent to People Operations for review.");
        navigate(`/compass/plans/${data.plan.id}`);
      } else {
        toast.success("Draft saved.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setTimelineDays = (n) => {
    setForm((f) => ({ ...f, timeline_days: n, check_in_dates: [] }));
  };

  if (!entry) return <p className="italic text-charcoal-muted">Loading entry request…</p>;
  if (entry.status !== "approved") {
    return (
      <div className="patria-card p-6">
        <p className="text-terracotta-700 font-medium">This entry request is <strong>{entry.status.replace("_", " ")}</strong>.</p>
        <p className="mt-1 text-sm text-charcoal-soft">A plan can only be built after PO has approved the entry request.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compass-plan-builder">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>
      <div>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">
          Build The Plan · {entry.employee_name}
        </h1>
        <p className="text-sm text-charcoal-muted">Approved entry request · {entry.gap_category?.replace("_", " ")}</p>
      </div>

      {/* Gap */}
      <div className="patria-card p-6 space-y-4">
        <SectionHeading n={1} title="The Gap" hint="Exact behaviors or results falling short, with evidence, examples, dates." />
        <SpecificityHint tip={warnings.gap} />
        <textarea
          className="patria-input w-full min-h-[130px]"
          value={form.gap_text}
          onChange={(e) => setForm({ ...form, gap_text: e.target.value })}
          data-testid="plan-gap-text"
        />
        <textarea
          className="patria-input w-full min-h-[90px]"
          placeholder="Additional evidence (dates, links, examples)"
          value={form.gap_evidence}
          onChange={(e) => setForm({ ...form, gap_evidence: e.target.value })}
          data-testid="plan-gap-evidence"
        />
      </div>

      {/* Standard */}
      <div className="patria-card p-6 space-y-4">
        <SectionHeading n={2} title="The Standard" hint="What meeting expectations looks like — tied to the job description." />
        <SpecificityHint tip={warnings.standard} />
        <textarea
          className="patria-input w-full min-h-[110px]"
          value={form.standard_text}
          onChange={(e) => setForm({ ...form, standard_text: e.target.value })}
          data-testid="plan-standard-text"
        />
        <input
          className="patria-input w-full"
          placeholder="Standard source (e.g., 'Worship Coordinator JD, section 3')"
          value={form.standard_source}
          onChange={(e) => setForm({ ...form, standard_source: e.target.value })}
          data-testid="plan-standard-source"
        />
      </div>

      {/* Measures */}
      <div className="patria-card p-6 space-y-3">
        <SectionHeading n={3} title="The Measure" hint="Objective, repeatable success measures. Each becomes a check-in line item." />
        {form.measures.map((m, idx) => {
          const w = (warnings.measures || []).find((mw) => mw.measure_id === m.id);
          return (
            <div key={m.id} className="rounded-2xl border border-sage-300/60 bg-sage-100/40 p-4" data-testid={`plan-measure-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <textarea
                    className="patria-input w-full min-h-[80px]"
                    placeholder="Objective success measure"
                    value={m.text}
                    onChange={(e) => setForm({ ...form, measures: form.measures.map((x, i) => i === idx ? { ...x, text: e.target.value } : x) })}
                    data-testid={`plan-measure-text-${idx}`}
                  />
                  {w?.warnings?.length > 0 && (
                    <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 text-xs" data-testid={`plan-measure-warning-${idx}`}>
                      <AlertTriangle className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
                      {w.warnings.join(" ")}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setForm({ ...form, measures: form.measures.filter((_, i) => i !== idx) })}
                  className="patria-btn-ghost text-terracotta-600"
                  disabled={form.measures.length <= 1}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          );
        })}
        <button
          className="patria-btn-secondary"
          onClick={() => setForm({ ...form, measures: [...form.measures, { id: uid(), text: "", notes: "" }] })}
          data-testid="plan-measure-add"
        >
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add measure
        </button>
      </div>

      {/* Timeline */}
      <div className="patria-card p-6 space-y-4">
        <SectionHeading n={4} title="The Timeline" hint="30 days → weekly · 60 or 90 days → biweekly. Auto-generated dates editable below." />
        <div className="flex gap-2 flex-wrap">
          {[30, 60, 90].map((n) => (
            <button
              key={n}
              className={`rounded-full px-4 py-2 text-sm font-medium ${form.timeline_days === n ? "bg-forest-600 text-cream-100" : "bg-cream-100 border border-sage-300 text-charcoal"}`}
              onClick={() => setTimelineDays(n)}
              data-testid={`plan-timeline-${n}`}
            >
              {n} days
            </button>
          ))}
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-charcoal-muted mb-2">Check-In Dates</p>
          {form.check_in_dates.length === 0 ? (
            <p className="text-sm italic text-charcoal-muted">Dates will be auto-generated when you save the draft.</p>
          ) : (
            <ul className="space-y-2">
              {form.check_in_dates.map((d, i) => (
                <li key={i} className="flex items-center gap-2">
                  <input
                    type="date"
                    className="patria-input"
                    value={d}
                    onChange={(e) => setForm({ ...form, check_in_dates: form.check_in_dates.map((v, ix) => ix === i ? e.target.value : v) })}
                    data-testid={`plan-checkin-date-${i}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
        <textarea
          className="patria-input w-full min-h-[90px]"
          placeholder="Resources & support committed"
          value={form.resources_and_support}
          onChange={(e) => setForm({ ...form, resources_and_support: e.target.value })}
          data-testid="plan-resources"
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          className="patria-btn-secondary"
          onClick={() => save({ submit: false })}
          disabled={saving}
          data-testid="plan-save-draft"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save draft
        </button>
        <button
          className="patria-btn-primary"
          onClick={() => save({ submit: true })}
          disabled={saving}
          data-testid="plan-submit-review"
        >
          <Send className="h-4 w-4" strokeWidth={1.5} /> Send to PO for review
        </button>
      </div>
    </div>
  );
}

function SectionHeading({ n, title, hint }) {
  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="h-8 w-8 rounded-full bg-forest-600 text-cream-100 flex items-center justify-center text-sm font-medium">
          {n}
        </span>
        <h2 className="text-xl font-serif text-forest-900">{title}</h2>
      </div>
      {hint && <p className="ml-11 text-xs text-charcoal-muted mt-1">{hint}</p>}
    </div>
  );
}

function SpecificityHint({ tip }) {
  if (!tip || (Array.isArray(tip) ? !tip.length : !tip)) return null;
  const arr = Array.isArray(tip) ? tip : [tip];
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 text-xs" data-testid="plan-specificity-warning">
      <AlertTriangle className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
      {arr.join(" ")}
    </div>
  );
}
